import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Check, Sparkles } from "lucide-react";

/**
 * Upsell combo suggestions. Fetches 3-4 items from
 * /api/products/{slug}/suggestions or /api/packages/{slug}/suggestions
 * and renders a grid with checkboxes. Notifies parent of selection via onChange.
 */
export default function ComboSuggestions({ slug, kind = "product", onChange }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    const url = kind === "package" ? `/packages/${slug}/suggestions` : `/products/${slug}/suggestions`;
    api.get(url)
      .then((r) => {
        setItems(r.data || []);
        setSelected({});
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug, kind]);

  useEffect(() => {
    // emit selected items (full product objects) to parent
    const chosen = items.filter((p) => selected[p.id]);
    onChange?.(chosen);
  }, [selected, items, onChange]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  if (loading || items.length === 0) return null;

  const getBasePrice = (p) => p.variants?.[0]?.price ?? p.price;

  return (
    <section className="mt-12 border-t border-arre-border pt-10" data-testid="combo-suggestions">
      <div className="flex items-start gap-3 mb-6">
        <Sparkles className="w-5 h-5 text-arre-red flex-shrink-0 mt-1" strokeWidth={1.5} />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-arre-red">⟡ Combo sugerido</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-wider uppercase text-white mt-1 leading-none">
            Completa tu asado, <span className="text-arre-red italic font-serif-display normal-case">compare</span>
          </h3>
          <p className="text-white/60 text-sm mt-2">
            Agrega lo que le da sabor. Lo mandamos todo junto sin costo extra de envío.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((p) => {
          const active = !!selected[p.id];
          const price = getBasePrice(p);
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.id)}
              className={`relative text-left border transition group ${
                active
                  ? "border-arre-red bg-arre-red/10"
                  : "border-arre-border bg-arre-surface hover:border-white/40"
              }`}
              data-testid={`combo-item-${p.slug}`}
            >
              <div className="aspect-square overflow-hidden bg-arre-black">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition"
                />
              </div>
              <div
                className={`absolute top-2 right-2 w-6 h-6 border-2 flex items-center justify-center transition ${
                  active ? "bg-arre-red border-arre-red" : "bg-arre-black/70 border-white/40"
                }`}
              >
                {active && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
              </div>
              <div className="p-3">
                <div className={`font-display text-base tracking-wider uppercase leading-tight ${
                  active ? "text-arre-red" : "text-white"
                }`}>
                  {p.name}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-white/50">
                    {p.variants?.[0]?.label?.split(" ")[0] || p.unit || ""}
                  </span>
                  <span className="font-display text-lg text-white">+{fmtMXN(price)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
